export interface CalculatorCategoryBreakdown {
  transport: number;
  housing: number;
  food: number;
  goods: number;
  services: number;
}

export interface CalculatorSnapshot {
  footprintMt: number;
  similarHouseholdAverageMt?: number;
  percentBelowSimilar?: number;
  categories: CalculatorCategoryBreakdown;
  /** Resolved household size (input_size 0 → 2.5 avg). */
  householdSize?: number;
}

const EMPTY_CATEGORIES: CalculatorCategoryBreakdown = {
  transport: 0,
  housing: 0,
  food: 0,
  goods: 0,
  services: 0
};

const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Calculator maps slider value 0 to a 2.5-person average household. */
export const resolveHouseholdSize = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const n = toNumber(value);
  if (n === 0) {
    return 2.5;
  }
  return n > 0 ? n : undefined;
};

export const isHouseholdFootprint = (householdSize?: number): boolean =>
  householdSize !== undefined && householdSize > 1;

export const hasCategoryBreakdown = (categories: CalculatorCategoryBreakdown): boolean =>
  Object.values(categories).some((value) => toNumber(value) > 0);

export const categorySumMt = (categories: CalculatorCategoryBreakdown): number => {
  const safe = categories || EMPTY_CATEGORIES;
  return toNumber(safe.transport)
    + toNumber(safe.housing)
    + toNumber(safe.food)
    + toNumber(safe.goods)
    + toNumber(safe.services);
};

/** Scale category bars so they sum exactly to targetMt (preserves relative mix). */
export const scaleCategoriesToTotal = (
  categories: CalculatorCategoryBreakdown,
  targetMt: number
): CalculatorCategoryBreakdown => {
  const safe = categories || EMPTY_CATEGORIES;
  const sum = categorySumMt(safe);
  const target = toNumber(targetMt);
  if (!(sum > 0) || !(target > 0)) {
    return { ...EMPTY_CATEGORIES };
  }
  const factor = target / sum;
  const scaled = {
    transport: Math.round(toNumber(safe.transport) * factor * 10) / 10,
    housing: Math.round(toNumber(safe.housing) * factor * 10) / 10,
    food: Math.round(toNumber(safe.food) * factor * 10) / 10,
    goods: Math.round(toNumber(safe.goods) * factor * 10) / 10,
    services: Math.round(toNumber(safe.services) * factor * 10) / 10
  };
  // Fix tenth-ton rounding drift on the largest category.
  const drift = Math.round((target - categorySumMt(scaled)) * 10) / 10;
  if (drift !== 0) {
    const keys: Array<keyof CalculatorCategoryBreakdown> = [
      'transport', 'housing', 'food', 'goods', 'services'
    ];
    let largest: keyof CalculatorCategoryBreakdown = 'transport';
    for (let i = 1; i < keys.length; i++) {
      if (scaled[keys[i]] > scaled[largest]) {
        largest = keys[i];
      }
    }
    scaled[largest] = Math.round((scaled[largest] + drift) * 10) / 10;
  }
  return scaled;
};

/** True when category bars and headline total describe the same footprint. */
export const snapshotTotalsMatch = (
  snapshot: CalculatorSnapshot,
  toleranceMt: number = 0.51
): boolean => {
  const normalized = normalizeCalculatorSnapshot(snapshot);
  if (!(normalized.footprintMt > 0) || !hasCategoryBreakdown(normalized.categories)) {
    return false;
  }
  return Math.abs(categorySumMt(normalized.categories) - normalized.footprintMt) <= toleranceMt;
};

export const normalizeCalculatorSnapshot = (snapshot?: CalculatorSnapshot | null | unknown): CalculatorSnapshot => {
  if (Array.isArray(snapshot)) {
    snapshot = {
      footprintMt: toNumber(snapshot[0]),
      similarHouseholdAverageMt: toNumber(snapshot[1]),
      percentBelowSimilar: toNumber(snapshot[2]),
      categories: (snapshot[3] as CalculatorCategoryBreakdown) || EMPTY_CATEGORIES
    };
  }
  const safe = (snapshot as CalculatorSnapshot) || { footprintMt: 0, categories: EMPTY_CATEGORIES };
  const categories = safe.categories || EMPTY_CATEGORIES;
  const footprintMt = toNumber(safe.footprintMt);
  return { ...safe, footprintMt, categories };
};

const SIMILAR_HOUSEHOLD_AVERAGE_MT = 19.6;

const pick = (source: Record<string, unknown>, keys: string[]): number => {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) {
      return toNumber(value);
    }
  }
  return 0;
};

const pickFirstValue = (source: Record<string, unknown>, keys: string[]): unknown => {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
};

export const parseCalculatorSnapshot = (ev: Record<string, unknown>): CalculatorSnapshot => {
  const nested = (ev.userFootprint as Record<string, unknown>)
    || (ev.user_footprint as Record<string, unknown>)
    || {};
  const footprintObj = (typeof ev.footprint === 'object' && ev.footprint)
    ? (ev.footprint as Record<string, unknown>)
    : {};
  const compute = (nested.computeFootprint as Record<string, unknown>)
    || (nested.compute_footprint as Record<string, unknown>)
    || (ev.computeFootprint as Record<string, unknown>)
    || (ev.compute_footprint as Record<string, unknown>)
    || (nested.compute_takeaction_results as Record<string, unknown>)
    || (nested.computeTakeActionResults as Record<string, unknown>)
    || footprintObj
    || {};
  const breakdown = (ev.breakdown as Record<string, unknown>)
    || (ev.categories as Record<string, unknown>)
    || (ev.totals as Record<string, unknown>)
    || {};
  const categoryPick = (keys: string[]): number => {
    for (const source of [compute, breakdown, nested, ev]) {
      for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
          return toNumber(source[key]);
        }
      }
    }
    return 0;
  };
  const categories: CalculatorCategoryBreakdown = {
    transport: categoryPick([
      'result_transport_total', 'resultTransportTotal', 'transport', 'transportTotal', 'travel', 'Travel'
    ]),
    housing: categoryPick([
      'result_housing_total', 'resultHousingTotal', 'housing', 'housingTotal', 'home', 'Home'
    ]),
    food: categoryPick([
      'result_food_total', 'resultFoodTotal', 'food', 'foodTotal', 'Food'
    ]),
    goods: categoryPick([
      'result_goods_total', 'resultGoodsTotal', 'goods', 'goodsTotal', 'Goods'
    ]),
    services: categoryPick([
      'result_services_total', 'resultServicesTotal', 'services', 'servicesTotal', 'Services'
    ])
  };
  let categorySum = 0;
  categorySum += categories.transport;
  categorySum += categories.housing;
  categorySum += categories.food;
  categorySum += categories.goods;
  categorySum += categories.services;
  const footprintMt = pick(ev, ['value', 'grandTotal', 'footprint', 'result_grand_total', 'resultGrandTotal'])
    || pick(compute, ['result_grand_total', 'resultGrandTotal', 'grandTotal'])
    || categorySum;
  const similarHouseholdAverageMt = pick(ev, [
    'similarHouseholdAverage',
    'averageFootprint',
    'average',
    'baselineFootprint',
    'nationalAverage'
  ]) || SIMILAR_HOUSEHOLD_AVERAGE_MT;
  let percentBelowSimilar = pick(ev, [
    'percentBelowSimilar',
    'percentBelowAverage',
    'percentDifference',
    'belowSimilarPercent'
  ]);
  if (!percentBelowSimilar && similarHouseholdAverageMt > 0 && footprintMt > 0) {
    percentBelowSimilar = ((similarHouseholdAverageMt - footprintMt) / similarHouseholdAverageMt) * 100;
  }
  const inputs = (ev.inputs as Record<string, unknown>)
    || ((nested.inputs as Record<string, unknown>) || {});
  const fromInputs = pickFirstValue(inputs, ['input_size', 'inputSize']);
  const householdSizeSource = fromInputs !== undefined
    ? fromInputs
    : pickFirstValue(ev, ['input_size', 'inputSize', 'householdSize']);
  const householdSize = resolveHouseholdSize(householdSizeSource);
  return normalizeCalculatorSnapshot({
    footprintMt,
    similarHouseholdAverageMt,
    percentBelowSimilar: Math.max(0, Math.round(percentBelowSimilar)),
    categories,
    householdSize
  });
};

export const mergeCalculatorAnswersIntoSnapshot = (
  snapshot: CalculatorSnapshot,
  answers: Record<string, unknown>
): CalculatorSnapshot => {
  const userFootprint = (answers.user_footprint as Record<string, unknown>)
    || (answers.userFootprint as Record<string, unknown>)
    || {};
  const compute = (userFootprint.compute_footprint as Record<string, unknown>)
    || (userFootprint.computeFootprint as Record<string, unknown>)
    || (userFootprint.compute_takeaction_results as Record<string, unknown>)
    || (userFootprint.computeTakeActionResults as Record<string, unknown>)
    || {};
  if (Object.keys(compute).length === 0) {
    return snapshot;
  }
  const inputs = (userFootprint.inputs as Record<string, unknown>) || {};
  const parsed = parseCalculatorSnapshot({
    value: snapshot.footprintMt,
    householdSize: snapshot.householdSize,
    inputs,
    user_footprint: userFootprint,
    compute_footprint: compute
  });
  return {
    ...snapshot,
    footprintMt: parsed.footprintMt > 0 ? parsed.footprintMt : snapshot.footprintMt,
    categories: hasCategoryBreakdown(parsed.categories) ? parsed.categories : snapshot.categories,
    householdSize: parsed.householdSize !== undefined ? parsed.householdSize : snapshot.householdSize
  };
};

export const formatComparisonText = (snapshot: CalculatorSnapshot): string => {
  const normalized = normalizeCalculatorSnapshot(snapshot);
  const roundedFootprint = normalized.footprintMt.toFixed(1);
  if (normalized.percentBelowSimilar && normalized.percentBelowSimilar > 0) {
    return `Your carbon footprint is ${roundedFootprint} metric tons CO₂e, ${normalized.percentBelowSimilar}% below similar households.`;
  }
  return `Your carbon footprint is ${roundedFootprint} metric tons CO₂e.`;
};

export const formatFootprintStoryIntro = (snapshot: CalculatorSnapshot): string => {
  const normalized = normalizeCalculatorSnapshot(snapshot);
  const footprint = normalized.footprintMt.toFixed(2);
  if (isHouseholdFootprint(normalized.householdSize)) {
    return `My household carbon footprint is ${footprint} metric tons CO₂e.`;
  }
  if (normalized.householdSize !== undefined && normalized.householdSize <= 1) {
    return `My personal carbon footprint is ${footprint} metric tons CO₂e.`;
  }
  return `My carbon footprint is ${footprint} metric tons CO₂e.`;
};

export const formatShareStoryText = (snapshot: CalculatorSnapshot, reflection?: string): string => {
  const base = formatFootprintStoryIntro(snapshot);
  const trimmed = (reflection || '').trim();
  if (!trimmed) {
    return base;
  }
  return `${base}\n\n${trimmed}`;
};
